// @flareapp/flare-client posts its reports from the browser straight to Flare, past the server's
// filter (App\Support\Flare\RedactSecrets). A report carries document.cookie, the page URL, the
// referrer and every query value. For a frame in an inline script it also quotes about 40 lines
// of the page's HTML, which it downloads again: the CSRF token, Livewire's wire:snapshot, a reset
// link's hidden token. This is its beforeSubmit hook, written against the report shape of
// flare-client 3.0.5 (package-lock.json). See documentation/context/flare-redaction.md.

const REDACTED = '<CENSORED>';

// A query or fragment parameter's value. Every one goes and the names stay: on-call rarely needs a
// value to debug a browser error, and a rule by name misses a secret under any other name.
const PARAMETER_VALUE = /([?&#][^=&#\s"'<>]+=)[^&#\s"'<>]+/g;

// Without the page's secret paths, a URL keeps only its origin.
const URL_PATH = /(\bhttps?:\/\/[^/?#\s"'<>]+)\/[^?#\s"'<>]*/gi;

// Only a script's quoted source is kept. Any other frame is in the page itself.
const SCRIPT_FILE = /\.m?js$/i;

const PAGE_SOURCE_NOT_SENT = "Not sent: this frame is in the page's HTML, which can hold secrets.";

/**
 * @param {object} report what the client hands to beforeSubmit
 * @param {string[]} secretPaths window.aaa_env.FLARE_SECRET_PATHS (RedactSecrets::secretPathPatterns())
 * @returns {object|false} the report to send; never throws, because the client would report that
 *                         error and fail on it again
 */
export function redactReport(report, secretPaths) {
    try {
        const scrub = scrubber(secretPaths);
        const { stacktrace = [], ...rest } = report;
        const redacted = scrubStrings(rest, scrub);
        const context = redacted.context ?? {};
        const page = withoutQuery(report.context?.request?.url);

        if (context.cookies) {
            context.cookies = censorValues(context.cookies);
        }

        if (context.request_data?.queryString) {
            context.request_data.queryString = censorValues(context.request_data.queryString);
        }

        // A script's quote is public code, kept as it is. A frame in the page's own inline script
        // names the page's URL as its file, whatever that URL ends in.
        redacted.stacktrace = stacktrace.map(({ code_snippet, ...frame }) => ({
            ...scrubStrings(frame, scrub),
            code_snippet: isScript(frame.file) && withoutQuery(frame.file) !== page
                ? code_snippet
                : { 0: PAGE_SOURCE_NOT_SENT },
        }));

        return redacted;
    } catch (error) {
        try {
            return failedReport(report, error);
        } catch {
            return false;
        }
    }
}

// The filter goes on before the key. The client sends nothing until light() gives it one, so no
// report can leave unfiltered. Flare runs on beta and production only, so local work spends no quota.
export function startFlare(flare, env) {
    flare.beforeSubmit = report => redactReport(report, env?.FLARE_SECRET_PATHS);

    if (env?.APP_ENV && ['production', 'beta'].includes(env.APP_ENV)) {
        flare.light(env.FLARE_JS_PUBLIC_KEY);
    }
}

function scrubber(secretPaths) {
    const paths = Array.isArray(secretPaths) ? secretPaths.map(pattern => new RegExp(pattern, 'gi')) : null;

    return text => {
        const cut = paths
            ? paths.reduce((result, path) => result.replace(path, `$1${REDACTED}`), text)
            : text.replace(URL_PATH, `$1/${REDACTED}`);

        return cut.replace(PARAMETER_VALUE, `$1${REDACTED}`);
    };
}

function scrubStrings(value, scrub) {
    if (typeof value === 'string') {
        return scrub(value);
    }

    if (Array.isArray(value)) {
        return value.map(item => scrubStrings(item, scrub));
    }

    if (value !== null && typeof value === 'object') {
        return Object.fromEntries(Object.entries(value).map(([key, item]) => [key, scrubStrings(item, scrub)]));
    }

    return value;
}

function censorValues(values) {
    return Object.fromEntries(Object.keys(values).map(name => [name, REDACTED]));
}

function isScript(file) {
    return typeof file === 'string' && SCRIPT_FILE.test(withoutQuery(file));
}

function withoutQuery(url) {
    return typeof url === 'string' ? url.split(/[?#]/)[0] : null;
}

// Sent in place of a report the filter could not redact, so on-call can tell a broken filter from
// a quiet page. It carries nothing from the page.
function failedReport(report, error) {
    return {
        notifier: report?.notifier,
        exception_class: 'FlareRedactionFailed',
        seen_at: report?.seen_at,
        message: `A browser error report was not sent, because redacting it failed (${error?.name ?? 'Error'}).`,
        language: 'javascript',
        glows: [],
        context: {},
        stacktrace: [{
            line_number: 0,
            column_number: 0,
            method: 'unknown',
            file: 'unknown',
            code_snippet: { 0: 'Not sent.' },
            trimmed_column_number: null,
            class: 'unknown',
        }],
        sourcemap_version_id: report?.sourcemap_version_id,
        solutions: [],
        stage: report?.stage,
    };
}
